import {
  Divider,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import {
  AccessConditionEffect,
  type Condition,
  type AccessCondition,
  type Role,
} from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';

import type { OptionMenuItemType } from '@/components/option-menu/option-menu';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { getRoleConditionSupportedProperties } from './utils';

import { ConditionEditor } from '../conditions/ConditionEditor';
import { BottomDialog } from '../dialogs/bottomDialog';
import { PanelFooter } from '../panels/panelFooter';
import { PanelHeader } from '../panels/panelHeader';

export const CustomRoleConditionEditorDialog = ({
  open,
  onSave,
  onCancel,
  roleCondition,
  accessConditions,
}: {
  roleCondition: AccessCondition | undefined;
  accessConditions: Role['accessConditions'];
  open: boolean;
  onCancel: () => void;
  onSave: (name: string, condition: Condition, isInclusive: boolean) => void;
}) => {
  const { SensitivityAnnotations: enableSensitivityAnnotations } =
    useFeatureFlags();
  const supportedProperties = useMemo(
    () =>
      getRoleConditionSupportedProperties({
        enableSensitivityAnnotations: !!enableSensitivityAnnotations,
      }),
    [enableSensitivityAnnotations]
  );
  const [name, setName] = useState(roleCondition?.id || '');
  const [isInclusive, setInclusive] = useState(
    roleCondition
      ? roleCondition.effect === AccessConditionEffect.RULE_EFFECT_INCLUSIVE
      : false
  );
  const [condition, setCondition] = useState(
    roleCondition ? structuredClone(roleCondition.condition) : undefined
  );

  const saveEnabled =
    name &&
    condition &&
    (roleCondition
      ? roleCondition.id === name
        ? true
        : !accessConditions?.find((x) => x.id === name)
      : !accessConditions?.find((x) => x.id === name));

  return (
    <BottomDialog open={open} isFullScreen={true}>
      <Stack className='h-full'>
        <PanelHeader
          hideBorder
          title={
            roleCondition ? 'Edit Access Condition' : 'Create Access Condition'
          }
          onClose={onCancel}
        />
        <Stack gap='12px' className='px-[24px] overflow-y-auto' flexGrow={1}>
          <Stack
            direction='row'
            className='mt-[24px] mx-[16px]'
            flexGrow={1}
            gap='40px'
          >
            <Stack flexGrow={1} gap='24px'>
              <Stack direction='row' alignItems='center' gap='8px'>
                <InclusiveOptionsMenu
                  isInclusive={isInclusive}
                  setInclusive={setInclusive}
                />
                <Typography variant='body1'>
                  access to resources that meet these conditions:
                </Typography>
              </Stack>

              <ConditionEditor
                options={{
                  supportGrouping: true,
                  supportedProperties,
                }}
                condition={condition}
                onChange={(condition) => setCondition(condition)}
              />
            </Stack>
            <Divider orientation='vertical' flexItem />
            <Stack className='w-[388px]'>
              <FormControl size='small'>
                <FormLabel>Name</FormLabel>
                <TextField
                  placeholder='Example: No production with PII'
                  className='my-[12px]'
                  size='small'
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </FormControl>
            </Stack>
          </Stack>
        </Stack>
        <PanelFooter
          primaryButton={{
            'data-testid': 'save-condition',
            text: roleCondition ? 'Save Changes' : 'Create Condition',
            onClick: () => onSave(name, condition!, isInclusive),
            disabled: !saveEnabled,
          }}
          secondaryButton={{
            'data-testid': 'cancel-edit-condition',
            text: 'Cancel',
            onClick: onCancel,
          }}
        />
      </Stack>
    </BottomDialog>
  );
};

const optionsBuilder = (selectedIndex: number): OptionMenuItemType[] =>
  [
    { text: 'Block', id: 'custom-role-block' },
    { text: 'Allow', id: 'custom-role-allow' },
  ].map((x, index) => ({
    menuItemProps: {
      sx: {
        ...(selectedIndex === index
          ? {
              backgroundColor: 'var(--mui-palette-primary-main)',
              color: 'var(--mui-palette-background-default)',
            }
          : {}),

        '&:hover': {
          backgroundColor:
            selectedIndex === index
              ? 'var(--mui-palette-primary-main)'
              : 'var(--mui-palette-background-primary)',
        },
      },
    },
    text: (
      <Typography className='pr-[8px]' color='currentColor'>
        {x.text}
      </Typography>
    ),
    id: x.id,
  }));

const InclusiveOptionsMenu = ({
  isInclusive,
  setInclusive,
}: {
  isInclusive: boolean;
  setInclusive: (value: boolean) => void;
}) => {
  const options = optionsBuilder(isInclusive ? 1 : 0);
  return (
    <Select
      value={isInclusive ? 'allow' : 'block'}
      onChange={(e) => setInclusive(e.target.value === 'allow')}
      size='small'
      sx={{
        width: '80px',
        '& .MuiSelect-select': {
          color: isInclusive
            ? 'var(--mui-palette-success-main)'
            : 'var(--mui-palette-error-main)',
          backgroundColor: isInclusive
            ? 'var(--mui-palette-success-bgLight)'
            : 'var(--mui-palette-error-bgLight)',
          transition:
            'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) !important',
        },
        '& .material-symbols-arrow-drop-down-rounded': {
          color: isInclusive
            ? 'var(--mui-palette-success-main)'
            : 'var(--mui-palette-error-main)',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '&:hover .MuiSelect-select': {
          backgroundColor: isInclusive
            ? 'var(--mui-palette-success-bgLightHover)'
            : 'var(--mui-palette-error-bgLightHover)',
        },
      }}
    >
      {options.map((option, index) => (
        <MenuItem
          key={option.id}
          value={index === 0 ? 'block' : 'allow'}
          sx={{
            ...(isInclusive === (index === 1)
              ? {
                  backgroundColor: 'var(--mui-palette-primary-main)',
                  color: 'var(--mui-palette-background-default)',
                }
              : {}),
            '&:hover': {
              backgroundColor:
                isInclusive === (index === 1)
                  ? 'var(--mui-palette-primary-main)'
                  : 'var(--mui-palette-background-primary)',
            },
          }}
        >
          {option.text}
        </MenuItem>
      ))}
    </Select>
  );
};
